/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ProductDetails, Filters, Sort } from '../../models/product';
import { filterAndSortProducts } from '../../utils/productFilters';
import Product from '../../components/Product';
import { Grid, Container, Alert } from '@mui/material';
import ProductModal from '../../components/ProductModal';
import ProductSkeletonGrid from '../../components/ProductSkeleton';
import SearchBar from '../../components/SearchBar';
import EmptyResults from '../../components/EmptyResults';
import { ProductContext } from '../../context/products-context';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { ALL_CATEGORIES } from '../../constants';

const Products = () => {
  const [modelIsOpen, setModalIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState<Filters>({
    minimumPrice: '',
    maximumPrice: '',
    categories: ALL_CATEGORIES.reduce<Record<string, boolean>>((acc, key) => {
      acc[key] = false;
      return acc;
    }, {}),
  });
  const [sort, setSort] = useState<Sort>('Default');

  const { products, isLoading, productsError } = useContext(ProductContext);
  const searchBarRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const HEADER_HEIGHT = 80;

  useEffect(() => {
    if (location.pathname === '/products' && !isLoading && searchBarRef.current) {
      const searchBarBottom = searchBarRef.current.getBoundingClientRect().bottom + window.scrollY;
      window.scrollTo({ top: Math.max(0, searchBarBottom - HEADER_HEIGHT), behavior: 'auto' });
    }
  }, [location.pathname, isLoading]);

  const [modalData, setModalData] = useState<ProductDetails>({
    name: '',
    description: '',
    price: 0,
    images: [],
    categories: [],
  });

  const filteredProducts = filterAndSortProducts(products, filters, searchText, sort);

  return (
    <Container sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }} maxWidth={false} disableGutters>
      <Header />
      <Container component="main" sx={{ flex: 1, pt: '80px' }} maxWidth={false} disableGutters>
        {productsError && (
          <Alert severity="error" sx={{ mx: 2, mb: 2 }}>
            {productsError}
          </Alert>
        )}
        {isLoading ? (
          <ProductSkeletonGrid />
        ) : (
          <>
            <Container ref={searchBarRef} maxWidth="lg" disableGutters sx={{ px: { xs: 2, md: 4, lg: 6 } }}>
              <SearchBar
                searchText={searchText}
                setSearchText={setSearchText}
                filters={filters}
                setFilters={setFilters}
                sort={sort}
                setSort={setSort}
              />
            </Container>
            {filteredProducts.length === 0 ? (
              <EmptyResults />
            ) : (
              <Grid container spacing={4} px={{ xs: 1, md: 2, lg: 4 }} data-testid="product-grid">
                {filteredProducts.map((product: ProductDetails) => (
                  <Grid size={{ lg: 4, md: 6, xs: 12 }} px={{ xs: 1, md: 2, lg: 4 }} key={product.name}>
                    <Product setModalIsOpen={setModalIsOpen} setModalData={setModalData} {...product} />
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}
        <ProductModal {...{ ...modalData, show: modelIsOpen, setModalIsOpen }} />
      </Container>
      <Footer />
    </Container>
  );
};

export default Products;